import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Question, X, Trophy, Users, Lightning, Target, Scales, Calendar, Crown, ArrowsDownUp, UserCircle, GearSix } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const sections = [
  {
    id: "tournaments",
    title: "Tournaments",
    icon: Trophy,
    content: [
      { heading: "Create a Tournament", text: "Go to Admin > Tournaments > 'Setup Wizard'. Follow the 5-step wizard: enter details (name, format, dates), select venue, pick players, assign organizers, and review. Fixtures and schedule are auto-generated on creation." },
      { heading: "Tournament Formats", text: "Knockout: Seeded bracket (top players don't meet early). Groups + Knockout: Players split into balanced groups, top advance to knockout. League: Round-robin, everyone plays everyone." },
      { heading: "Auto-Scheduling", text: "After creation, matches are automatically assigned dates, times, and tables based on venue availability, player availability, and scheduling settings (35min/frame + 10min buffer, weekday start 7PM). You can also manually trigger scheduling from the Tournament Detail page." },
      { heading: "Tournament Progression", text: "When all matches in a round are completed, the next round is automatically generated (e.g., QF complete → SF generated). Players are notified of their next match." },
    ]
  },
  {
    id: "live",
    title: "Live Scoring",
    icon: Lightning,
    content: [
      { heading: "Start a Live Match", text: "Go to Admin > Live tab. Select a tournament — all unplayed scheduled matches appear. Click 'Go Live' on any match. Optionally add a YouTube live stream URL." },
      { heading: "Update Scores", text: "Once live, select the match from the Live Scoring section. Enter frame number, points, breaks, and select the frame winner. Click 'Update Score'. The match auto-completes when a player wins enough frames." },
      { heading: "End Match Early", text: "Use the 'Player Wins' buttons to end a match before all frames are played." },
      { heading: "Record Results (Admin > Results)", text: "For matches already completed offline, go to Admin > Results. Select tournament, expand any match, enter frame scores directly, and click 'Save Result'. You can also edit existing completed results." },
    ]
  },
  {
    id: "players",
    title: "Players",
    icon: Users,
    content: [
      { heading: "Add Players", text: "Admin > Players tab > 'Add Player'. Enter name, mobile, ITS number, select tier (Premier/Challenger). Toggle 'External' for players from other clubs." },
      { heading: "Player Categories", text: "Admin > Settings > Player Categories. Create custom tiers, set max players per tier, assign colors. Default: Premier (top players) and Challenger." },
      { heading: "Claim Profile", text: "Players sign in with Google and request to claim their profile from the Members page. Admins approve/reject claims in Admin > Claims tab. Once approved, the player can view their personal stats and upcoming matches." },
      { heading: "Player Availability", text: "Admin > Players tab > 'Player Availability' section. Set unavailable dates per player — the scheduler will avoid those dates." },
    ]
  },
  {
    id: "rankings",
    title: "Rankings",
    icon: Crown,
    content: [
      { heading: "How Rankings Work", text: "Players are ranked by total tournament points accumulated over a 2-year rolling window. Points are awarded based on tournament placement (Winner: 1000, Runner-up: 700, SF: 400, QF: 200) with multipliers for tournament type." },
      { heading: "Manual Rankings", text: "Admin > Rankings tab > 'Edit Rankings'. Drag players to reorder, or use the dropdown to move a player to a specific rank. This shifts everyone in between (not a swap). Click 'Save Rankings' to persist." },
      { heading: "ELO System", text: "Separate from club rankings. ELO updates automatically after each match. All players start at a unified 1500 baseline (world standard) and rise or fall based on opponent strength, frame margin, and upsets. View in Rankings > ELO tab." },
    ]
  },
  {
    id: "matchpoints",
    title: "Match Points",
    icon: Target,
    content: [
      { heading: "How Match Points Work", text: "Every completed match awards points to both players based on performance. Base: Win = 20pts, Loss = 5pts." },
      { heading: "Ranking Multiplier", text: "Beating a much higher-ranked player (upset) = 2.0x. Beating a slightly higher player = 1.2-1.5x. Beating lower-ranked = 0.8-0.9x. Losing to lower-ranked = 0.5x penalty." },
      { heading: "Handicap Adjustment", text: "Player with handicap disadvantage gets 1.35x bonus. Player with advantage gets 0.75x reduction. Even = 1.0x." },
      { heading: "Frame Bonus", text: "Win 3-0 (clean sweep) = +20%. Win 3-1 = +10%. Win 3-2 = no bonus." },
      { heading: "Streak Bonus", text: "+10% bonus after 3 consecutive wins." },
      { heading: "Bounds", text: "Minimum 10 points, maximum 50 points per match." },
    ]
  },
  {
    id: "scheduling",
    title: "Scheduling",
    icon: Calendar,
    content: [
      { heading: "Scheduling Settings", text: "Admin > Settings > Scheduling. Configure: minutes per frame (default 35), buffer between matches (10min), weekday/weekend start times, default tables." },
      { heading: "Venue Management", text: "Admin > Venues. Add venues with table count and weekly availability (which days, start/end times). The scheduler uses this to allocate tables." },
      { heading: "Constraints", text: "1 match per player per day. No overlapping matches on same table. Player unavailable dates respected. Locked matches skipped during rescheduling." },
      { heading: "Rescheduling", text: "Admin can reschedule any match from the Tournament Detail page or Admin > Results. Conflicts are auto-detected and shifted to the next available slot." },
      { heading: "Lock Fixtures", text: "Lock specific matches to prevent them from being moved by auto-rescheduling." },
    ]
  },
  {
    id: "handicap",
    title: "Handicap",
    icon: Scales,
    content: [
      { heading: "Handicap System", text: "Admin > Handicap tab. Click 'Load Suggestions' to auto-generate handicap values based on player win rates and rankings." },
      { heading: "Apply Handicaps", text: "Review suggestions and click 'Apply All', or manually set individual player handicaps. Handicap affects match points calculation." },
    ]
  },
  {
    id: "admin",
    title: "Admin",
    icon: GearSix,
    content: [
      { heading: "Adding Admins", text: "Admin emails are configured in the system. Contact the primary admin to add new admin emails. Admins can manage all aspects of the platform." },
      { heading: "Disputes", text: "Admin > Disputes tab. Players can dispute match results. Admins can confirm or void disputed matches." },
      { heading: "Referees", text: "Admin > Referees tab. Approve referee requests and assign referees to live matches." },
      { heading: "Seasons", text: "Admin > Settings > Seasons. Create seasons with start/end dates. Link tournaments to seasons. View season stats and leaderboards." },
      { heading: "Point Settings", text: "Admin > Settings > Point Allocation. Configure winner/runner-up/SF/QF points and tournament type multipliers." },
    ]
  },
];

export default function HelpManual() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        className="h-9 w-9 p-0 rounded-full border border-border hover:border-primary/40 hover:bg-primary/5"
        data-testid="help-manual-btn"
        aria-label="Help & Guide"
      >
        <Question className="w-5 h-5 text-muted-foreground" weight="bold" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-2">
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Question className="w-6 h-6 text-primary" weight="fill" />
              Annadil Burhani DSM Snooker — Guide
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="tournaments" className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="bg-muted/50 w-full overflow-x-auto flex-nowrap justify-start shrink-0">
              {sections.map(s => (
                <TabsTrigger key={s.id} value={s.id} className="text-xs gap-1">
                  <s.icon className="w-3.5 h-3.5" weight="fill" />{s.title}
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="flex-1 overflow-y-auto mt-3 pr-1">
              {sections.map(s => (
                <TabsContent key={s.id} value={s.id} className="mt-0 space-y-4">
                  {s.content.map((item, i) => (
                    <div key={`${s.id}-${i}`} className="space-y-1">
                      <h4 className="text-sm font-semibold text-foreground">{item.heading}</h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">{item.text}</p>
                    </div>
                  ))}
                </TabsContent>
              ))}
            </div>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
}
